import { useState, useEffect } from 'react';
import {
  Globe, RefreshCw, Clock, CheckCircle2, AlertCircle,
  Radio, ExternalLink, Users, Filter, Eye, Target,
} from 'lucide-react';

import { useTheme, useFeatureFlags } from '../components/Navbar';
import API_BASE from '../config';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isMobile;
}

const TK = {
  light: {
    bg: '#ffffff', card: '#ffffff', border: '#ECEEF2',
    text: '#0F1623', sub: '#6B7280', muted: '#9CA3AF',
    accent: '#5B4EE8', accentBg: '#EEF0FD', accentTxt: '#4338CA',
    hover: '#F7F8FA',
    success: '#059669', successBg: '#d1fae5', successBorder: '#a7f3d0',
    danger: '#dc2626', dangerBg: '#fee2e2', dangerBorder: '#fecaca',
    blue: '#1877F2', blueBg: '#e7f0fe',
    neutralBg: '#F5F6FA', neutralText: '#374151',
    inputBg: '#ffffff',
    pauseBg: '#fef3c7', pauseBorder: '#fde68a', pauseText: '#92400e',
  },
  dark: {
    bg: '#0D0D14', card: '#131320', border: '#1E1E2E',
    text: '#F1F2F6', sub: '#8B8FA8', muted: '#4B5263',
    accent: '#7C72FF', accentBg: 'rgba(124,114,255,0.12)', accentTxt: '#A5A0FF',
    hover: '#1A1A2A',
    success: '#34d399', successBg: 'rgba(16,185,129,0.1)', successBorder: 'rgba(52,211,153,0.2)',
    danger: '#f87171', dangerBg: 'rgba(239,68,68,0.08)', dangerBorder: 'rgba(239,68,68,0.2)',
    blue: '#5B9BFF', blueBg: 'rgba(59,130,246,0.1)',
    neutralBg: '#1A1A2A', neutralText: '#d1d5db',
    inputBg: '#1A1A2A',
    pauseBg: 'rgba(245,158,11,0.1)', pauseBorder: 'rgba(251,191,36,0.2)', pauseText: '#fbbf24',
  },
};

const toIST = (utcString) => {
  if (!utcString) return '—';
  let s = String(utcString).trim().replace(' ', 'T');
  if (!/Z$|[+-]\d{2}:\d{2}$/.test(s)) s += 'Z';
  const d = new Date(s);
  if (isNaN(d)) return '—';
  return d.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

export default function ChannelSync() {
  const isMobile = useIsMobile();
  const { theme } = useTheme();
  const C = TK[theme];
  const { flags } = useFeatureFlags();
  const facebookSyncOn = flags.facebook_sync !== false;
  const audiencePushOn = flags.facebook_audience_sync !== false;
  const googleSyncOn = flags.google_sync !== false;

  const token = localStorage.getItem('martech_token') || localStorage.getItem('token');

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');
  const [lastResult, setLastResult] = useState(null);

  const [segments, setSegments] = useState([]);
  const [selectedSegments, setSelectedSegments] = useState([]);
  const [dateField, setDateField] = useState('last_seen');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [previewCount, setPreviewCount] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  const [matchStatus, setMatchStatus] = useState(null);

  const [googleLogs, setGoogleLogs] = useState([]);
  const [googleLoading, setGoogleLoading] = useState(true);
  const [googleSyncing, setGoogleSyncing] = useState(false);
  const [googleError, setGoogleError] = useState('');
  const [googleLastResult, setGoogleLastResult] = useState(null);
  const [googleSegments, setGoogleSegments] = useState([]);
  const [googleSelectedSegments, setGoogleSelectedSegments] = useState([]);
  const [googleDateField, setGoogleDateField] = useState('last_seen');
  const [googleDateFrom, setGoogleDateFrom] = useState('');
  const [googleDateTo, setGoogleDateTo] = useState('');
  const [googlePreviewCount, setGooglePreviewCount] = useState(null);
  const [googlePreviewing, setGooglePreviewing] = useState(false);
  const [checkingMatch, setCheckingMatch] = useState(false);

  const currentFilters = () => ({
    segment_names: selectedSegments,
    date_field: dateField,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
  });

  const fetchSegments = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/v1/facebook/segments`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await res.json();
      if (d.success) setSegments(d.segments);
    } catch (e) {
      // non-fatal
    }
  };

  const fetchLogs = async () => {
    try {
      setError('');
      const res = await fetch(`${API_BASE}/api/v1/facebook/sync-log`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await res.json();
      if (d.success) setLogs(d.logs);
      else setError(d.error || 'Failed to load sync history');
    } catch (e) {
      setError('Network error loading sync history');
    } finally {
      setLoading(false);
    }
  };

  const fetchMatchStatus = async () => {
    setCheckingMatch(true);
    try {
      const res = await fetch(`${API_BASE}/api/v1/facebook/audience-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await res.json();
      if (d.success) setMatchStatus(d);
      else setError(d.error || 'Could not check match status');
    } catch (e) {
      setError('Network error checking match status');
    } finally {
      setCheckingMatch(false);
    }
  };

  useEffect(() => { if (facebookSyncOn) { fetchSegments(); fetchLogs(); fetchMatchStatus(); } else { setLoading(false); } }, [facebookSyncOn]); // eslint-disable-line react-hooks/exhaustive-deps

  const googleCurrentFilters = () => ({
    segment_names: googleSelectedSegments,
    date_field: googleDateField,
    date_from: googleDateFrom || undefined,
    date_to: googleDateTo || undefined,
  });

  const fetchGoogleSegments = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/v1/google/segments`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await res.json();
      if (d.success) setGoogleSegments(d.segments);
    } catch (e) {
      // non-fatal
    }
  };

  const fetchGoogleLogs = async () => {
    try {
      setGoogleError('');
      const res = await fetch(`${API_BASE}/api/v1/google/sync-log`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await res.json();
      if (d.success) setGoogleLogs(d.logs);
      else setGoogleError(d.error || 'Failed to load sync history');
    } catch (e) {
      setGoogleError('Network error loading sync history');
    } finally {
      setGoogleLoading(false);
    }
  };

  const toggleGoogleSegment = (name) => {
    setGooglePreviewCount(null);
    setGoogleSelectedSegments((prev) => (prev.includes(name) ? prev.filter((s) => s !== name) : [...prev, name]));
  };

  const handleGooglePreview = async () => {
    setGooglePreviewing(true);
    setGoogleError('');
    try {
      const res = await fetch(`${API_BASE}/api/v1/google/preview-audience`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(googleCurrentFilters()),
      });
      const d = await res.json();
      if (d.success) setGooglePreviewCount(d.count);
      else setGoogleError(d.error || 'Preview failed');
    } catch (e) {
      setGoogleError('Network error during preview');
    } finally {
      setGooglePreviewing(false);
    }
  };

  const handleGoogleSyncNow = async () => {
    setGoogleSyncing(true);
    setGoogleError('');
    setGoogleLastResult(null);
    try {
      const res = await fetch(`${API_BASE}/api/v1/google/sync-customer-match`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(googleCurrentFilters()),
      });
      const d = await res.json();
      if (d.success) {
        setGoogleLastResult(d);
        fetchGoogleLogs();
      } else {
        setGoogleError(d.error || 'Sync failed');
      }
    } catch (e) {
      setGoogleError('Network error during sync');
    } finally {
      setGoogleSyncing(false);
    }
  };

  useEffect(() => { if (googleSyncOn) { fetchGoogleSegments(); fetchGoogleLogs(); } else { setGoogleLoading(false); } }, [googleSyncOn]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSegment = (name) => {
    setPreviewCount(null);
    setSelectedSegments((prev) => (prev.includes(name) ? prev.filter((s) => s !== name) : [...prev, name]));
  };

  const handlePreview = async () => {
    setPreviewing(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/v1/facebook/preview-audience`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(currentFilters()),
      });
      const d = await res.json();
      if (d.success) setPreviewCount(d.count);
      else setError(d.error || 'Preview failed');
    } catch (e) {
      setError('Network error during preview');
    } finally {
      setPreviewing(false);
    }
  };

  const handleSyncNow = async () => {
    if (!audiencePushOn) return;
    setSyncing(true);
    setError('');
    setLastResult(null);
    try {
      const res = await fetch(`${API_BASE}/api/v1/facebook/sync-audience`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(currentFilters()),
      });
      const d = await res.json();
      if (d.success) {
        setLastResult(d);
        fetchLogs();
      } else {
        setError(d.error || 'Sync failed');
        if (d.count) setLastResult(d);
      }
    } catch (e) {
      setError('Network error during sync');
    } finally {
      setSyncing(false);
    }
  };

  const cardStyle = { background: C.card, border: `1px solid ${C.border}`, borderRadius: '14px' };
  const inputStyle = { padding: '0.55rem 0.7rem', borderRadius: '8px', border: `1px solid ${C.border}`, background: C.inputBg, color: C.text, fontSize: '0.82rem' };

  const StatCard = ({ label, value, icon: Icon, color, bg }) => (
    <div style={{ ...cardStyle, padding: '1.1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
        <div style={{ width: '2.25rem', height: '2.25rem', borderRadius: '10px', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={18} color={color} />
        </div>
      </div>
      <p style={{ fontSize: '1.4rem', fontWeight: '800', margin: 0, color: C.text }}>{value}</p>
      <p style={{ fontSize: '0.75rem', color: C.sub, fontWeight: '500', margin: '0.15rem 0 0' }}>{label}</p>
    </div>
  );

  const th = { padding: '0.7rem 1rem', textAlign: 'left', fontSize: '0.7rem', fontWeight: '700', letterSpacing: '0.04em', textTransform: 'uppercase', color: C.sub, whiteSpace: 'nowrap' };
  const td = { padding: '0.7rem 1rem', fontSize: '0.85rem', whiteSpace: 'nowrap' };
  const latestLog = logs[0];

  return (
    <div style={{ background: C.bg, minHeight: '100%' }}>
      <div style={{ padding: isMobile ? '1rem' : '2rem 2.5rem', maxWidth: '1200px', margin: '0 auto', boxSizing: 'border-box' }}>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.75rem' }}>
          <div style={{ width: '2.5rem', height: '2.5rem', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.blueBg }}>
            <Radio size={20} color={C.blue} />
          </div>
          <div>
            <h1 style={{ color: C.text, fontSize: '1.4rem', fontWeight: '800', margin: 0, letterSpacing: '-0.01em' }}>Channel Sync</h1>
            <p style={{ color: C.sub, fontSize: '0.85rem', margin: '0.15rem 0 0' }}>Connect and sync your ad platform audiences</p>
          </div>
        </div>

        {error && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.75rem 1rem', borderRadius: '10px', marginBottom: '1.25rem', fontSize: '0.85rem', color: C.danger, background: C.dangerBg, border: `1px solid ${C.dangerBorder}` }}>
            <AlertCircle size={16} /> {error}
          </div>
        )}

        {!facebookSyncOn ? null : (
        <div style={{ ...cardStyle, padding: isMobile ? '1.25rem' : '1.5rem', marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.25rem' }}>
            <div style={{ width: '2.5rem', height: '2.5rem', borderRadius: '12px', background: C.blueBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Globe size={19} color={C.blue} />
            </div>
            <div>
              <h2 style={{ color: C.text, fontWeight: '800', fontSize: '0.95rem', margin: 0 }}>Facebook Custom Audience</h2>
              <p style={{ color: C.sub, fontSize: '0.78rem', margin: '0.1rem 0 0' }}>Sync a hashed Custom Audience to Meta Ads for the profiles you choose below</p>
            </div>
          </div>

          {!audiencePushOn && (
            <div style={{ padding: '0.85rem 1rem', borderRadius: '10px', background: C.pauseBg, border: `1px solid ${C.pauseBorder}`, marginBottom: '1.25rem', fontSize: '0.82rem', color: C.pauseText }}>
              Pushing to Facebook is temporarily paused (ad account under review). You can still preview matching profile counts below — syncing will resume once this is re-enabled.
            </div>
          )}

          <div style={{ background: C.neutralBg, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '1rem', marginBottom: '1.25rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.85rem' }}>
              <Filter size={14} color={C.sub} />
              <span style={{ fontSize: '0.72rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', color: C.sub }}>Filters</span>
            </div>

            {segments.length > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                <p style={{ fontSize: '0.78rem', fontWeight: '600', color: C.text, marginBottom: '0.5rem' }}>Segments (leave empty for all profiles)</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {segments.map((s) => {
                    const active = selectedSegments.includes(s.segment_name);
                    return (
                      <button
                        key={s.segment_name}
                        onClick={() => toggleSegment(s.segment_name)}
                        style={{
                          padding: '0.4rem 0.75rem', borderRadius: '999px', fontSize: '0.78rem', fontWeight: '600', cursor: 'pointer',
                          border: `1px solid ${active ? C.accent : C.border}`,
                          background: active ? C.accentBg : C.card,
                          color: active ? C.accentTxt : C.sub,
                        }}
                      >
                        {s.segment_name.replace(/_/g, ' ')} ({s.member_count})
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: '0.75rem' }}>
              <div>
                <p style={{ fontSize: '0.78rem', fontWeight: '600', color: C.text, marginBottom: '0.4rem' }}>Date field</p>
                <select value={dateField} onChange={(e) => { setDateField(e.target.value); setPreviewCount(null); }} style={{ ...inputStyle, width: '100%' }}>
                  <option value="last_seen">Last Active</option>
                  <option value="created_at">Date Created</option>
                </select>
              </div>
              <div>
                <p style={{ fontSize: '0.78rem', fontWeight: '600', color: C.text, marginBottom: '0.4rem' }}>From</p>
                <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPreviewCount(null); }} style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }} />
              </div>
              <div>
                <p style={{ fontSize: '0.78rem', fontWeight: '600', color: C.text, marginBottom: '0.4rem' }}>To</p>
                <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPreviewCount(null); }} style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }} />
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '1rem', flexWrap: 'wrap' }}>
              <button
                onClick={handlePreview}
                disabled={previewing}
                style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.55rem 1rem', borderRadius: '8px', fontSize: '0.8rem', fontWeight: '600', color: C.text, background: C.card, border: `1px solid ${C.border}`, cursor: previewing ? 'not-allowed' : 'pointer' }}
              >
                <Eye size={14} /> {previewing ? 'Checking...' : 'Preview Count'}
              </button>
              {previewCount !== null && (
                <span style={{ fontSize: '0.85rem', fontWeight: '700', color: C.accent }}>
                  {previewCount} profile{previewCount === 1 ? '' : 's'} match these filters
                </span>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1.25rem' }}>
            <button
              onClick={handleSyncNow}
              disabled={syncing || !audiencePushOn}
              style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.6rem 1.1rem', borderRadius: '10px', fontSize: '0.85rem', fontWeight: '700', color: '#fff', background: C.accent, border: 'none', cursor: (syncing || !audiencePushOn) ? 'not-allowed' : 'pointer', opacity: (syncing || !audiencePushOn) ? 0.6 : 1, whiteSpace: 'nowrap' }}
            >
              <RefreshCw size={15} style={syncing ? { animation: 'spin 1s linear infinite' } : {}} />
              {syncing ? 'Syncing...' : !audiencePushOn ? 'Sync Paused' : 'Sync Now'}
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.85rem', marginBottom: '1.25rem' }}>
            <StatCard label="Last Push Count" value={latestLog ? latestLog.record_count : '—'} icon={Users} color={C.blue} bg={C.blueBg} />
            <StatCard label="Last Sync Status" value={latestLog ? latestLog.status?.replace(/_/g, ' ') : '—'} icon={CheckCircle2} color={C.success} bg={C.successBg} />
            <StatCard label="Last Synced" value={latestLog ? toIST(latestLog.synced_at) : 'Never'} icon={Clock} color={C.sub} bg={C.neutralBg} />
          </div>

          <div style={{ padding: '1rem', borderRadius: '10px', background: C.neutralBg, border: `1px solid ${C.border}`, marginBottom: '1.25rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                <Target size={16} color={C.accent} />
                <span style={{ fontSize: '0.82rem', fontWeight: '700', color: C.text }}>Matched Audience Size (Meta)</span>
              </div>
              <button
                onClick={fetchMatchStatus}
                disabled={checkingMatch}
                style={{ fontSize: '0.75rem', fontWeight: '600', padding: '0.35rem 0.7rem', borderRadius: '7px', background: C.card, border: `1px solid ${C.border}`, color: C.sub, cursor: checkingMatch ? 'not-allowed' : 'pointer' }}
              >
                {checkingMatch ? 'Checking...' : 'Check Match Status'}
              </button>
            </div>
            <p style={{ fontSize: '0.8rem', color: C.sub, margin: '0.6rem 0 0' }}>
              {!matchStatus || !matchStatus.has_audience
                ? 'No Facebook Custom Audience created yet — run a sync first.'
                : matchStatus.ready
                ? `Approximately ${matchStatus.approx_count_lower?.toLocaleString('en-IN')}–${matchStatus.approx_count_upper?.toLocaleString('en-IN')} people matched on Facebook.`
                : "Audience created, but Meta hasn't finished matching yet — this can take a few hours after a sync. Check back later."}
            </p>
          </div>

          {lastResult && (
            <div style={{ padding: '1rem', borderRadius: '10px', background: lastResult.status === 'push_failed' || error ? C.dangerBg : C.successBg, border: `1px solid ${lastResult.status === 'push_failed' || error ? C.dangerBorder : C.successBorder}`, marginBottom: '1.25rem' }}>
              <p style={{ color: lastResult.status === 'push_failed' ? C.danger : C.success, fontSize: '0.85rem', fontWeight: '700', margin: '0 0 0.4rem' }}>
                {lastResult.count} profile{lastResult.count === 1 ? '' : 's'} hashed{lastResult.status === 'pushed_to_facebook' ? ' and pushed to Facebook' : ''}
              </p>
              {lastResult.note && <p style={{ color: C.text, fontSize: '0.8rem', margin: 0 }}>{lastResult.note}</p>}
              
                <a
                href="https://business.facebook.com/adsmanager/audiences"
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', marginTop: '0.6rem', fontSize: '0.8rem', fontWeight: '600', color: C.blue, textDecoration: 'none' }}
              >
                Open Meta Ads Manager <ExternalLink size={12} />
              </a>
            </div>
          )}

          <p style={{ color: C.sub, fontSize: '0.7rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.75rem' }}>Sync History</p>
          {loading ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: C.sub, fontSize: '0.85rem' }}>Loading...</div>
          ) : logs.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: C.muted, fontSize: '0.85rem', background: C.neutralBg, borderRadius: '10px', border: `1px solid ${C.border}` }}>
              No syncs yet — set your filters above and click "Sync Now".
            </div>
          ) : isMobile ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
              {logs.map((log) => (
                <div key={log.id} style={{ background: C.neutralBg, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '0.85rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
                    <span style={{ fontWeight: '600', fontSize: '0.85rem', color: C.text }}>{log.record_count} profiles</span>
                    <span style={{ fontSize: '0.7rem', fontWeight: '700', padding: '0.15rem 0.5rem', borderRadius: '999px', background: C.successBg, color: C.success, textTransform: 'capitalize' }}>{log.status?.replace(/_/g, ' ')}</span>
                  </div>
                  <div style={{ fontSize: '0.75rem', color: C.muted, marginTop: '0.2rem' }}>{toIST(log.synced_at)}</div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ border: `1px solid ${C.border}`, borderRadius: '10px', overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '500px' }}>
                  <thead style={{ background: C.neutralBg }}>
                    <tr>{['Type', 'Profiles', 'Status', 'Synced At (IST)'].map((h) => <th key={h} style={th}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {logs.map((log, i) => (
                      <tr key={log.id} style={{ borderTop: i === 0 ? 'none' : `1px solid ${C.border}` }}>
                        <td style={{ ...td, color: C.text, textTransform: 'capitalize' }}>{log.sync_type?.replace(/_/g, ' ')}</td>
                        <td style={{ ...td, color: C.text, fontWeight: '600' }}>{log.record_count}</td>
                        <td style={td}>
                          <span style={{ fontSize: '0.75rem', fontWeight: '700', padding: '0.15rem 0.55rem', borderRadius: '999px', background: C.successBg, color: C.success, textTransform: 'capitalize' }}>{log.status?.replace(/_/g, ' ')}</span>
                        </td>
                        <td style={{ ...td, color: C.sub, fontSize: '0.78rem' }}>{toIST(log.synced_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
        )}

        {!googleSyncOn ? null : (
        <div style={{ ...cardStyle, padding: isMobile ? '1.25rem' : '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.25rem' }}>
            <div style={{ width: '2.5rem', height: '2.5rem', borderRadius: '12px', background: C.neutralBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Target size={19} color={C.neutralText} />
            </div>
            <div>
              <h2 style={{ color: C.text, fontWeight: '800', fontSize: '0.95rem', margin: 0 }}>Google Ads Customer Match</h2>
              <p style={{ color: C.sub, fontSize: '0.78rem', margin: '0.1rem 0 0' }}>Sync a hashed customer list for Google Ads for the profiles you choose below</p>
            </div>
          </div>

          {googleError && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.75rem 1rem', borderRadius: '10px', marginBottom: '1.25rem', fontSize: '0.85rem', color: C.danger, background: C.dangerBg, border: `1px solid ${C.dangerBorder}` }}>
              <AlertCircle size={16} /> {googleError}
            </div>
          )}

          <div style={{ background: C.neutralBg, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '1rem', marginBottom: '1.25rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.85rem' }}>
              <Filter size={14} color={C.sub} />
              <span style={{ fontSize: '0.72rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', color: C.sub }}>Filters</span>
            </div>

            {googleSegments.length > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                <p style={{ fontSize: '0.78rem', fontWeight: '600', color: C.text, marginBottom: '0.5rem' }}>Segments (leave empty for all profiles)</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {googleSegments.map((s) => {
                    const active = googleSelectedSegments.includes(s.segment_name);
                    return (
                      <button
                        key={s.segment_name}
                        onClick={() => toggleGoogleSegment(s.segment_name)}
                        style={{
                          padding: '0.4rem 0.75rem', borderRadius: '999px', fontSize: '0.78rem', fontWeight: '600', cursor: 'pointer',
                          border: `1px solid ${active ? C.accent : C.border}`,
                          background: active ? C.accentBg : C.card,
                          color: active ? C.accentTxt : C.sub,
                        }}
                      >
                        {s.segment_name.replace(/_/g, ' ')} ({s.member_count})
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: '0.75rem' }}>
              <div>
                <p style={{ fontSize: '0.78rem', fontWeight: '600', color: C.text, marginBottom: '0.4rem' }}>Date field</p>
                <select value={googleDateField} onChange={(e) => { setGoogleDateField(e.target.value); setGooglePreviewCount(null); }} style={{ ...inputStyle, width: '100%' }}>
                  <option value="last_seen">Last Active</option>
                  <option value="created_at">Date Created</option>
                </select>
              </div>
              <div>
                <p style={{ fontSize: '0.78rem', fontWeight: '600', color: C.text, marginBottom: '0.4rem' }}>From</p>
                <input type="date" value={googleDateFrom} onChange={(e) => { setGoogleDateFrom(e.target.value); setGooglePreviewCount(null); }} style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }} />
              </div>
              <div>
                <p style={{ fontSize: '0.78rem', fontWeight: '600', color: C.text, marginBottom: '0.4rem' }}>To</p>
                <input type="date" value={googleDateTo} onChange={(e) => { setGoogleDateTo(e.target.value); setGooglePreviewCount(null); }} style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }} />
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '1rem', flexWrap: 'wrap' }}>
              <button
                onClick={handleGooglePreview}
                disabled={googlePreviewing}
                style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.55rem 1rem', borderRadius: '8px', fontSize: '0.8rem', fontWeight: '600', color: C.text, background: C.card, border: `1px solid ${C.border}`, cursor: googlePreviewing ? 'not-allowed' : 'pointer' }}
              >
                <Eye size={14} /> {googlePreviewing ? 'Checking...' : 'Preview Count'}
              </button>
              {googlePreviewCount !== null && (
                <span style={{ fontSize: '0.85rem', fontWeight: '700', color: C.accent }}>
                  {googlePreviewCount} profile{googlePreviewCount === 1 ? '' : 's'} match these filters
                </span>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1.25rem' }}>
            <button
              onClick={handleGoogleSyncNow}
              disabled={googleSyncing}
              style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.6rem 1.1rem', borderRadius: '10px', fontSize: '0.85rem', fontWeight: '700', color: '#fff', background: C.accent, border: 'none', cursor: googleSyncing ? 'not-allowed' : 'pointer', opacity: googleSyncing ? 0.6 : 1, whiteSpace: 'nowrap' }}
            >
              <RefreshCw size={15} style={googleSyncing ? { animation: 'spin 1s linear infinite' } : {}} />
              {googleSyncing ? 'Syncing...' : 'Sync Now'}
            </button>
          </div>

          {googleLastResult && (
            <div style={{ padding: '1rem', borderRadius: '10px', background: C.successBg, border: `1px solid ${C.successBorder}`, marginBottom: '1.25rem' }}>
              <p style={{ color: C.success, fontSize: '0.85rem', fontWeight: '700', margin: '0 0 0.4rem' }}>
                {googleLastResult.count} profile{googleLastResult.count === 1 ? '' : 's'} hashed and ready for upload
              </p>
              <p style={{ color: C.text, fontSize: '0.8rem', margin: 0 }}>{googleLastResult.note}</p>
              
                <a
                href="https://ads.google.com/aw/audiences"
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', marginTop: '0.6rem', fontSize: '0.8rem', fontWeight: '600', color: C.accent, textDecoration: 'none' }}
              >
                Open Google Ads Audience Manager <ExternalLink size={12} />
              </a>
            </div>
          )}

          <p style={{ color: C.sub, fontSize: '0.7rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.75rem' }}>Sync History</p>
          {googleLoading ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: C.sub, fontSize: '0.85rem' }}>Loading...</div>
          ) : googleLogs.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: C.muted, fontSize: '0.85rem', background: C.neutralBg, borderRadius: '10px', border: `1px solid ${C.border}` }}>
              No syncs yet — set your filters above and click "Sync Now".
            </div>
          ) : isMobile ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
              {googleLogs.map((log) => (
                <div key={log.id} style={{ background: C.neutralBg, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '0.85rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
                    <span style={{ fontWeight: '600', fontSize: '0.85rem', color: C.text }}>{log.record_count} profiles</span>
                    <span style={{ fontSize: '0.7rem', fontWeight: '700', padding: '0.15rem 0.5rem', borderRadius: '999px', background: C.successBg, color: C.success, textTransform: 'capitalize' }}>{log.status}</span>
                  </div>
                  <div style={{ fontSize: '0.75rem', color: C.muted, marginTop: '0.2rem' }}>{toIST(log.synced_at)}</div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ border: `1px solid ${C.border}`, borderRadius: '10px', overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '500px' }}>
                  <thead style={{ background: C.neutralBg }}>
                    <tr>{['Type', 'Profiles', 'Status', 'Synced At (IST)'].map((h) => <th key={h} style={th}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {googleLogs.map((log, i) => (
                      <tr key={log.id} style={{ borderTop: i === 0 ? 'none' : `1px solid ${C.border}` }}>
                        <td style={{ ...td, color: C.text, textTransform: 'capitalize' }}>{log.sync_type?.replace(/_/g, ' ')}</td>
                        <td style={{ ...td, color: C.text, fontWeight: '600' }}>{log.record_count}</td>
                        <td style={td}>
                          <span style={{ fontSize: '0.75rem', fontWeight: '700', padding: '0.15rem 0.55rem', borderRadius: '999px', background: C.successBg, color: C.success, textTransform: 'capitalize' }}>{log.status}</span>
                        </td>
                        <td style={{ ...td, color: C.sub, fontSize: '0.78rem' }}>{toIST(log.synced_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
        )}

        <div style={{ height: '2rem' }} />
      </div>
    </div>
  );
}
